/**
 * Marked 마크다운 파서 설정
 * 이 파일은 marked 라이브러리의 진입점 역할을 합니다.
 */

import { escape } from 'lodash-es';
import { Marked, Renderer, Tokenizer } from 'marked';
import type { MarkedExtension, MarkedOptions, RendererObject, Token, TokenizerAndRendererExtension } from 'marked';
import type { MarkdownConfig, RichMarkdownTableCell, RichMarkdownTableData, RichMarkdownTableRow } from './types';
import { createRichMarkdownTableCell, encodeMarkdownTablePayload, normalizeRichMarkdownTableRows } from './tableRenderer';

// 전처리 함수
import {
    createInlineCodeProtector,
    normalizeEmphasisDelimiters,
    escapeLeadingDateForMarkdown,
    normalizeIndentedLists,
    normalizeMarkdownTables,
    normalizeBacktickedLinks,
    fixBrokenMarkdownLinks,
    normalizeLinksWithParentheses,
} from './preprocessors';

// 후처리 함수
import { replaceLastMargin } from './postprocessors';

// 플러그인
import { cjkStrongPlugin, lheadingPlugin } from './plugins';

// ============================================================================
// href 정규화 (XSS 방지)
// ============================================================================

export const sanitizeHref = (raw: string): string | null => {
    const href = String(raw || '').trim();
    if (!href) return null;

    // HTML 특수문자 차단 (속성 탈출 공격 방지)
    if (/["'<>]/.test(href)) return null;

    // 위험 프로토콜 체크용 정규화 (공백/entities 디코딩)
    const normalized = href
        .replace(/\0/g, '')
        .replace(/\s/g, '')
        .replace(/&#x([0-9a-f]+);?/gi, (_, h) => String.fromCharCode(parseInt(h, 16)))
        .replace(/&#(\d+);?/g, (_, d) => String.fromCharCode(parseInt(d, 10)))
        .replace(/&(colon|#58|#x3a);?/gi, ':');

    let decoded = normalized;
    try {
        decoded = decodeURIComponent(normalized);
    } catch {}

    // 위험 프로토콜 및 이벤트 핸들러 차단
    if (/^(?:javascript|data|vbscript|file|about|blob):/i.test(decoded) || /\bon\w+\s*=/i.test(normalized)) {
        return null;
    }

    return href;
};

// ============================================================================
// HTML Sanitization (XSS 방지, 정상 HTML 허용)
// ============================================================================

// 허용 태그 화이트리스트
// 외부에서 sanitizeHtml 을 직접 호출하면서 화이트리스트를 확장해야 할 때 합칠 수 있도록 export.
// 호출자는 setup 시 1회만 합친 Set 을 만들어 sanitizeHtml 에 넘기는 것이 권장.
export const ALLOWED_TAGS: ReadonlySet<string> = new Set([
    'br',
    'p',
    'strong',
    'em',
    'mark',
    'b',
    'i',
    'u',
    's',
    'del',
    'ul',
    'ol',
    'li',
    'table',
    'tr',
    'td',
    'th',
    'thead',
    'tbody',
    'tfoot',
    'a',
    'img',
    'blockquote',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'pre',
    'code',
    'span',
    'div',
    'hr',
    'sup',
    'sub',
    'button', // Legacy citation 지원용
]);

// 허용 속성 화이트리스트
const ALLOWED_ATTRS = new Set([
    'class',
    'id',
    'href',
    'src',
    'alt',
    'title',
    'target',
    'rel',
    'colspan',
    'rowspan',
    'width',
    'height',
    'type', // button type 등
    // Legacy citation 지원용 속성
    'data-citation-index',
    'data-chat-index',
    'data-tooltip-title',
    'data-citation-document-start-guid',
    'data-citation-document-end-guid',
]);

/**
 * 속성 문자열에서 위험 속성(on* 이벤트, javascript: 등) 제거
 */
const filterDangerousAttrs = (attrString: string): string => {
    if (!attrString || !attrString.trim()) return '';

    // 속성 파싱: name="value" 또는 name='value' 또는 name=value 형태
    const attrPattern = /([a-z][a-z0-9_-]*)\s*(?:=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+)))?/gi;
    const safeAttrs: string[] = [];
    let match;

    while ((match = attrPattern.exec(attrString)) !== null) {
        const attrName = match[1].toLowerCase();
        const attrValue = match[2] ?? match[3] ?? match[4] ?? '';

        // on* 이벤트 핸들러 차단
        if (attrName.startsWith('on')) continue;

        // 허용 속성만 통과
        if (!ALLOWED_ATTRS.has(attrName)) continue;

        // href/src 속성은 추가 검증
        if (attrName === 'href' || attrName === 'src') {
            const safeValue = sanitizeHref(attrValue);
            if (!safeValue) continue;
            // 값 내 HTML entity escape (XSS 방지)
            safeAttrs.push(`${attrName}="${escape(safeValue)}"`);
        } else {
            // 값 내 HTML entity escape (XSS 방지)
            const escapedValue = escape(attrValue);
            safeAttrs.push(`${attrName}="${escapedValue}"`);
        }
    }

    return safeAttrs.length > 0 ? ' ' + safeAttrs.join(' ') : '';
};

/**
 * HTML을 sanitize: 허용 태그는 통과, 위험 태그/속성은 escape 처리.
 *
 * `allowedTags` 가 전달되면 기본 ALLOWED_TAGS 대신 그것을 화이트리스트로 사용한다.
 * 호출자가 ALLOWED_TAGS 전역을 건드리지 않고 특정 사용처(예: 해외 법령 본문)에서만 태그
 * 화이트리스트를 확장하기 위한 hook 이며, 매 호출마다 머지 비용을 들이지 않도록 setup
 * 시점에 1회만 합쳐 만든 Set 을 그대로 넘기는 것이 권장이다.
 */
export const sanitizeHtml = (html: string, allowedTags: ReadonlySet<string> = ALLOWED_TAGS): string => {
    if (!html) return '';

    // HTML 주석 제거
    const strippedOfComments = html.replace(/<!--[\s\S]*?-->/g, '');

    // 태그 파싱: <tagname ...> 또는 </tagname> 또는 <tagname ... />
    // 정규식 개선: 속성값 내의 '>' 문자가 태그 종료로 오인되지 않도록 따옴표 처리 강화
    return strippedOfComments.replace(/<\/?([a-z][a-z0-9]*)\b((?:[^>"']|"[^"]*"|'[^']*')*)?\/?>/gi, (match, tagName, attrs = '') => {
        const lowerTag = tagName.toLowerCase();

        // 허용되지 않은 태그 → escape 처리 (텍스트로 노출)
        if (!allowedTags.has(lowerTag)) {
            return escape(match);
        }

        // 닫는 태그
        if (match.startsWith('</')) {
            return `</${lowerTag}>`;
        }

        // 자체 닫는 태그 (br, hr, img 등)
        const isSelfClosing = /\/\s*>$/.test(match);
        const safeAttrs = filterDangerousAttrs(attrs);

        // HTML로 직접 온 <a> 태그에 스타일/클래스/인라인스타일이 전혀 없을 때만 기본 링크 스타일 적용
        if (lowerTag === 'a' && !isSelfClosing) {
            const hasClass = /\bclass\s*=/i.test(attrs);
            const hasStyle = /\bstyle\s*=/i.test(attrs);
            const hasTarget = /\btarget\s*=/i.test(attrs);
            const isLegislationLink = /href\s*=\s*["'][^"']*legislation[^"']*["']/i.test(attrs);
            const linkClass = !hasClass && !hasStyle ? ' class="text-indigo-500 underline font-semibold"' : '';
            const linkTarget = hasTarget || isLegislationLink ? '' : ' target="_blank" rel="noopener noreferrer"';
            return `<a${safeAttrs}${linkClass}${linkTarget}>`;
        }

        return isSelfClosing ? `<${lowerTag}${safeAttrs} />` : `<${lowerTag}${safeAttrs}>`;
    });
};

// ============================================================================
// Marked 인스턴스 설정
// ============================================================================

interface IMarkedInstance {
    use: (...extensions: MarkedExtension[]) => unknown;
    parse: (src: string, options?: MarkedOptions) => string | Promise<string>;
}

export const setupMarkedInstance = <T extends IMarkedInstance = Marked>({
    markedInstance = new Marked() as unknown as T,
    customRenderFunctionMap = {},
    config = {},
}: {
    markedInstance?: T;
    customRenderFunctionMap?: RendererObject;
    config?: MarkdownConfig;
} = {}): T => {
    const { tokenizer, extensions, customRenderers } = createTokenizerAndExtensions(config);

    const mergedCustomRenderers = { ...customRenderFunctionMap, ...customRenderers };
    const renderer = createRenderer(mergedCustomRenderers, config);

    // walkTokens 함수 - 모든 토큰을 순회하면서 &lt;br&gt; 태그를 줄바꿈으로 변환
    const walkTokens = (token: Token) => {
        if (token.type === 'text' && token.text) {
            token.text = token.text.replace(/&lt;br\s*\/?&gt;/gi, '\n');
        }
    };

    // marked에 토크나이저와 확장 기능 적용
    markedInstance.use({
        renderer,
        tokenizer: tokenizer as Tokenizer,
        walkTokens,
        extensions: extensions as any,
        gfm: true,
        breaks: true,
    });

    // parse 함수를 래핑하여 전처리/후처리 파이프라인 적용
    wrapParseFunction(markedInstance, config);

    return markedInstance;
};

// ============================================================================
// 렌더러 생성
// ============================================================================

// 렌더링 시마다 재생성하지 않도록 상수로 분리
const DEFAULT_HEADING_STYLES: Record<number, string> = {
    1: 'text-[1.75rem] font-bold mt-4 mb-0.5 text-gray-900 leading-[1.89]',
    2: 'text-[1.5rem] font-bold mt-6 mb-0.5 text-gray-900 leading-[1.7]',
    3: 'text-[1.25rem] font-semibold mt-4 mb-[0.675rem] text-gray-900 leading-[1.85]',
    4: 'text-base font-semibold mt-4 mb-[0.675rem] text-gray-900 leading-[1.75]',
    5: 'text-sm font-semibold mt-2.5 mb-0.5 text-gray-900 leading-[1.7]',
    6: 'text-xs font-semibold mt-2.5 mb-0.5 text-gray-900 leading-[1.65]',
};

const TABLE_CELL_MARKER = 'markdown-table-cell';
const TABLE_ROW_MARKER = 'markdown-table-row';

const createMarker = (name: string, value: unknown): string => `<!--${name}:${encodeMarkdownTablePayload(value)}-->`;

const readMarkers = <T>(html: string, name: string): T[] => {
    const pattern = new RegExp(`<!--${name}:([^>]*)-->`, 'g');
    const values: T[] = [];
    let match: RegExpExecArray | null;

    while ((match = pattern.exec(html)) !== null) {
        try {
            values.push(JSON.parse(decodeURIComponent(match[1])) as T);
        } catch {
            // Ignore malformed internal markers and continue rendering the rest.
        }
    }

    return values;
};

const applyRichTableRenderer = (renderer: Renderer): void => {
    let tableSeq = 0;

    renderer.tablecell = (content, flags) => createMarker(TABLE_CELL_MARKER, createRichMarkdownTableCell(content, flags.align ?? null));

    renderer.tablerow = content => {
        const cells = readMarkers<RichMarkdownTableCell>(content, TABLE_CELL_MARKER);
        return createMarker(TABLE_ROW_MARKER, cells);
    };

    renderer.table = (header, body) => {
        const headerRows = normalizeRichMarkdownTableRows(readMarkers<RichMarkdownTableRow>(header, TABLE_ROW_MARKER));
        const bodyRows = normalizeRichMarkdownTableRows(readMarkers<RichMarkdownTableRow>(body, TABLE_ROW_MARKER));
        const table: RichMarkdownTableData = {
            id: `markdown-table-${++tableSeq}`,
            headers: headerRows[0] ?? [],
            rows: bodyRows,
        };
        const encodedTable = encodeMarkdownTablePayload(table);

        return `<span data-markdown-table data-table-id="${table.id}" data-table="${escape(encodedTable)}"></span>`;
    };
};

const COMPACT_HEADING_STYLES: Record<number, string> = {
    1: 'text-2xl lg:text-3xl font-bold mt-4 mb-0.5 text-gray-900 leading-7 lg:leading-[1.89]',
    2: 'text-xl lg:text-2xl font-bold mt-6 mb-0.5 text-gray-900 leading-7 lg:leading-[1.7]',
    3: 'text-lg lg:text-xl font-semibold mt-4 mb-[0.675rem] text-gray-900 leading-7 lg:leading-[1.85]',
    4: 'text-sm lg:text-base font-semibold mt-4 mb-[0.675rem] text-gray-900 leading-6 lg:leading-[1.75]',
    5: 'text-xs lg:text-sm font-semibold mt-2.5 mb-0.5 text-gray-900 leading-5 lg:leading-[1.7]',
    6: 'text-xs font-semibold mt-2.5 mb-0.5 text-gray-900 leading-5 lg:leading-[1.65]',
};

const createRenderer = (customRenderFunctionMap: RendererObject = {}, config: MarkdownConfig = {}): Renderer => {
    const renderer = new Renderer();

    const effectiveHtmlMode: NonNullable<MarkdownConfig['htmlMode']> = config.htmlMode ?? (config.sanitizeHtml === false ? 'allow' : 'sanitize');
    const headingStyles = config.compact ? COMPACT_HEADING_STYLES : DEFAULT_HEADING_STYLES;
    const bodyTextClass = config.compact ? 'text-sm lg:text-base leading-7 lg:leading-[1.89]' : 'leading-[1.89]';
    const codeBlockClass = config.compact ? 'text-xs lg:text-sm text-gray-600 mb-4 whitespace-pre-wrap' : 'text-gray-600 mb-4 whitespace-pre-wrap';
    const inlineCodeClass = config.compact ? 'bg-gray-200 text-red-600 px-1 rounded text-xs lg:text-sm' : 'bg-gray-200 text-red-600 px-1 rounded';
    const blockquoteClass = config.compact
        ? 'border-l-4 border-blue-100 pl-4 italic text-gray-600 mb-4 text-sm lg:text-base leading-7 lg:leading-[1.89]'
        : 'border-l-4 border-blue-100 pl-4 italic text-gray-600 mb-4';

    // 코드 블록은 항상 escape 처리 (XSS 방지)
    const sanitizeCode = (code: string) => escape(code);

    // 디자이너 스타일 가이드 적용
    renderer.code = (code, language) => `<p class="${codeBlockClass}">${sanitizeCode(code)}</p>`;
    renderer.codespan = code => `<code class="${inlineCodeClass}">${sanitizeCode(code)}</code>`;
    renderer.listitem = text => `<li class="mb-1 text-gray-900${config.compact ? ' text-sm lg:text-base' : ''}">${text}</li>`;
    renderer.list = (body, ordered, start) => {
        const isKo = config.locale?.startsWith('ko');
        const listClass = ordered ? (isKo ? 'list-[hangul]' : 'list-decimal') : 'list-disc';
        const marginClass = ordered ? 'mb-3' : 'mb-3.5';
        const startAttr = typeof start === 'number' && start > 1 ? ` start="${start}"` : '';

        return `<${ordered ? 'ol' : 'ul'} class="${listClass} ${marginClass} pl-5 ${bodyTextClass} text-gray-900"${startAttr}>${body}</${ordered ? 'ol' : 'ul'}>`;
    };
    renderer.paragraph = text => `<p class="text-gray-900 whitespace-pre-wrap ${bodyTextClass}">${text}</p>`;
    renderer.heading = (text, level) => {
        return `<h${level} class="${headingStyles[level] || headingStyles[4]}">${text}</h${level}>`;
    };
    renderer.table = (header, body) => `<table class="w-full bg-white mb-8 border-none"><thead class="bg-white w-full">${header}</thead><tbody class="bg-white w-full">${body}</tbody></table>`;
    renderer.tablerow = content => `<tr class="border-b-2 border-gray-100">${content}</tr>`;
    renderer.tablecell = (content, flags) => {
        const cellClasses = flags.header
            ? `table-cell align-top bg-white p-2 border-none text-left leading-[1.6rem]${flags.align ? ` text-${flags.align}` : ''}`
            : `table-cell align-top p-3 leading-[1.6rem]${flags.align ? ` text-${flags.align}` : ''}`;
        return `<${flags.header ? 'th' : 'td'} class="${cellClasses}">${content}</${flags.header ? 'th' : 'td'}>`;
    };
    renderer.strong = text => `<strong class="font-semibold">${text}</strong>`;
    renderer.em = text => `<em class="italic">${text}</em>`;
    renderer.link = (href, title, text) => {
        if (!href) return text || '';
        const validHref = sanitizeHref(String(href));
        if (!validHref) return text || '';

        const safeTitle = escape(title || '');
        return `<a href="${validHref}" title="${safeTitle}" class="text-indigo-500 underline font-semibold" target="_blank" rel="noopener noreferrer">${text || validHref}</a>`;
    };
    renderer.image = (href, title, text) => {
        const validSrc = sanitizeHref(String(href || ''));
        if (!validSrc) return escape(String(text || ''));

        const safeAlt = escape(text || '');
        const safeTitle = escape(title || '');
        return `<img src="${validSrc}" alt="${safeAlt}" title="${safeTitle}" class="max-w-full h-auto mt-4 mb-4">`;
    };
    renderer.blockquote = quote => `<blockquote class="${blockquoteClass}">${quote}</blockquote>`;
    renderer.hr = () => '<hr class="border-t border-gray-100 mt-8 mb-8">';

    // HTML 처리 모드
    // - escape: 사용자 입력 등 신뢰할 수 없는 HTML을 문자 그대로 노출
    // - sanitize: 허용 태그는 유지하고 위험 요소만 제거 (기본값)
    // - allow: 원본 HTML 그대로 통과 (신뢰된 소스 전용)
    //
    // additionalAllowedTags 와 ALLOWED_TAGS 머지는 setup 시점에 1회만 수행하고, 매 renderer.html
    // 호출마다 재생성하지 않는다 (성능 최적화).
    const effectiveAllowedTags: ReadonlySet<string> =
        config.additionalAllowedTags && config.additionalAllowedTags.length > 0
            ? new Set<string>([...ALLOWED_TAGS, ...config.additionalAllowedTags.map(t => t.toLowerCase())])
            : ALLOWED_TAGS;
    if (effectiveHtmlMode === 'escape') {
        renderer.html = html => escape(html);
    } else if (effectiveHtmlMode === 'sanitize') {
        renderer.html = html => sanitizeHtml(html, effectiveAllowedTags);
    }

    if (customRenderFunctionMap) {
        for (const [key, value] of Object.entries(customRenderFunctionMap) as [keyof RendererObject, RendererObject[keyof RendererObject]][]) {
            (renderer[key] as unknown) = value;
        }
    }

    if (config.tableRenderer?.enabled) {
        applyRichTableRenderer(renderer);
    }

    return renderer;
};

// ============================================================================
// 토크나이저 및 확장 기능 생성
// ============================================================================

const createTokenizerAndExtensions = (config: MarkdownConfig): { tokenizer: Partial<Tokenizer>; extensions: TokenizerAndRendererExtension[]; customRenderers: RendererObject } => {
    const plugins = config.plugins || [];

    // 기본 플러그인 (전역 적용)
    if (!config.disableDefaultPlugins) {
        if (!plugins.some(p => p.name === 'lheading')) {
            plugins.push(lheadingPlugin);
        }
        if (!plugins.some(p => p.name === 'cjkStrong')) {
            plugins.push(cjkStrongPlugin);
        }
    }

    const tokenizer: Partial<Tokenizer> = {
        // 취소선(strikethrough) 기능 비활성화
        del() {
            return undefined;
        },
    };
    const extensions: TokenizerAndRendererExtension[] = [];
    const customRenderers: RendererObject = {};

    plugins.forEach(plugin => {
        if (plugin.tokenizer) {
            Object.assign(tokenizer, plugin.tokenizer);
        }
        if (plugin.extensions) {
            extensions.push(...plugin.extensions);
        }
        if (plugin.renderer) {
            Object.assign(customRenderers, plugin.renderer);
        }
    });

    return { tokenizer, extensions, customRenderers };
};

// ============================================================================
// Parse 함수 래핑 (전처리/후처리 파이프라인)
// ============================================================================

const wrapParseFunction = <T extends IMarkedInstance>(markedInstance: T, config: MarkdownConfig = {}): void => {
    const originalParse = markedInstance.parse.bind(markedInstance);

    markedInstance.parse = (markdown: string, options?: MarkedOptions) => {
        // 매 파싱 호출마다 독립적인 코드 보호기 생성 (동시성 문제 방지)
        const protector = createInlineCodeProtector();

        // 전처리 파이프라인
        const preprocessed = applyPreprocessors(markdown, protector.protect, config);

        // 원본 마크다운 변환 실행
        const html = originalParse(preprocessed, options);

        // 후처리 파이프라인 (마진 처리 + 코드 토큰 복원)
        const processHtml = (htmlStr: string): string => {
            const marginProcessed = replaceLastMargin(htmlStr);
            return protector.restore(marginProcessed);
        };

        if (typeof html === 'string') {
            return processHtml(html);
        } else if (html && typeof (html as Promise<string>).then === 'function') {
            return (html as Promise<string>).then(processHtml);
        }
        return html;
    };
};

/**
 * 마크다운 전처리 파이프라인
 * @param markdown - 원본 마크다운 문자열
 * @param protectInlineCode - 인라인 코드 보호 함수 (클로저로 전달)
 */
const applyPreprocessors = (markdown: string, protectInlineCode: (md: string) => string, config: MarkdownConfig = {}): string => {
    // 빈 문자열은 정상 입력, 비문자열 타입만 경고
    if (typeof markdown !== 'string') {
        console.warn('[applyPreprocessors] 문자열이 아닌 값 전달됨:', { type: typeof markdown, value: markdown });
        return '';
    }
    if (!markdown) return '';

    // escape 모드: 사용자 입력이므로 < > 를 HTML entity로 치환하여
    // 마크다운 blockquote(>)나 HTML 태그(<...>)로 해석되는 것을 방지
    if (config.htmlMode === 'escape') {
        markdown = markdown.replace(/</g, '&lt;').replace(/>/g, '&gt;');
    }

    // 1) 백틱으로 감싸진 링크 정규화
    const normalizedBacktickedLinks = normalizeBacktickedLinks(markdown);

    // 2) 깨진 마크다운 링크 복원 (LLM이 URL 괄호로 인해 잘못 생성한 링크)
    const fixedBrokenLinks = fixBrokenMarkdownLinks(normalizedBacktickedLinks);

    // 3) URL에 괄호가 있는 링크를 꺾쇠괄호로 감싸기 (marked.js 파싱 오류 방지)
    const normalizedParenthesesLinks = normalizeLinksWithParentheses(fixedBrokenLinks);

    // 4) 인라인 코드 보호
    const protectedMarkdown = protectInlineCode(normalizedParenthesesLinks);

    // 5) 강조 구분자 정규화
    const delimiterNormalizedMarkdown = normalizeEmphasisDelimiters(protectedMarkdown);

    // 6) 날짜 패턴 이스케이프 (ordered list 오인식 방지)
    const dateEscapedMarkdown = escapeLeadingDateForMarkdown(delimiterNormalizedMarkdown);

    // 7) 들여쓰기된 리스트 및 일반 텍스트 정규화 (code block 오인식 방지)
    const listNormalizedMarkdown = normalizeIndentedLists(dateEscapedMarkdown);

    // 8) 마크다운 테이블 정규화 (들여쓰기된 테이블을 파싱 가능하도록 outdent)
    return normalizeMarkdownTables(listNormalizedMarkdown);
};
